/**
 * A store-only ZIP writer (no compression) for the batch page's PDF file (WP-1b.3, BD-9): one
 * archive of PDFs built in the browser, with no new dependency. PDFs are already compressed, so
 * storing them costs little. Entry names are UTF-8 (general-purpose flag bit 11).
 *
 * `readStoredZip` reads such an archive back; the unit tests and the e2e use it to check the file.
 */

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

let crcTable: Uint32Array | null = null;
function table(): Uint32Array {
  if (crcTable) return crcTable;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  crcTable = t;
  return t;
}

/** CRC-32 (IEEE 802.3), as ZIP requires. */
export function crc32(data: Uint8Array): number {
  const t = table();
  let c = 0xffffffff;
  for (const byte of data) c = (t[(c ^ byte) & 0xff] ?? 0) ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** MS-DOS date and time (local clock, 2-second resolution). */
function dosDateTime(d: Date): { time: number; date: number } {
  const year = Math.min(Math.max(d.getFullYear(), 1980), 2107);
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

const LOCAL = 0x04034b50;
const CENTRAL = 0x02014b50;
const END = 0x06054b50;
const UTF8 = 0x0800;

/** Builds a store-only ZIP archive of the entries, in order. */
export function buildStoredZip(
  entries: readonly ZipEntry[],
  when: Date = new Date(),
): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const { time, date } = dosDateTime(when);
  const parts: { name: Uint8Array; data: Uint8Array; crc: number; offset: number }[] = [];
  let offset = 0;
  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    parts.push({ name, data: entry.data, crc: crc32(entry.data), offset });
    offset += 30 + name.length + entry.data.length;
  }
  const centralSize = parts.reduce((n, p) => n + 46 + p.name.length, 0);
  const out = new Uint8Array(offset + centralSize + 22);
  const view = new DataView(out.buffer);
  let at = 0;
  const u16 = (v: number) => {
    view.setUint16(at, v, true);
    at += 2;
  };
  const u32 = (v: number) => {
    view.setUint32(at, v >>> 0, true);
    at += 4;
  };
  const bytes = (b: Uint8Array) => {
    out.set(b, at);
    at += b.length;
  };
  for (const p of parts) {
    u32(LOCAL);
    u16(20); // version needed: 2.0
    u16(UTF8);
    u16(0); // method: stored
    u16(time);
    u16(date);
    u32(p.crc);
    u32(p.data.length); // compressed size = size (stored)
    u32(p.data.length);
    u16(p.name.length);
    u16(0); // no extra field
    bytes(p.name);
    bytes(p.data);
  }
  const centralStart = at;
  for (const p of parts) {
    u32(CENTRAL);
    u16(20); // version made by
    u16(20); // version needed
    u16(UTF8);
    u16(0);
    u16(time);
    u16(date);
    u32(p.crc);
    u32(p.data.length);
    u32(p.data.length);
    u16(p.name.length);
    u16(0); // extra
    u16(0); // comment
    u16(0); // disk number
    u16(0); // internal attributes
    u32(0); // external attributes
    u32(p.offset);
    bytes(p.name);
  }
  u32(END);
  u16(0);
  u16(0);
  u16(parts.length);
  u16(parts.length);
  u32(at - centralStart);
  u32(centralStart);
  u16(0); // comment length
  return out;
}

/** Reads a store-only ZIP (as written above) back into its entries; checks every CRC. */
export function readStoredZip(archive: Uint8Array): ZipEntry[] {
  const view = new DataView(archive.buffer, archive.byteOffset, archive.byteLength);
  const decoder = new TextDecoder();
  let end = archive.length - 22;
  while (end >= 0 && view.getUint32(end, true) !== END) end -= 1;
  if (end < 0) throw new Error('not a ZIP archive: no end of central directory');
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  const entries: ZipEntry[] = [];
  for (let i = 0; i < count; i++) {
    if (view.getUint32(at, true) !== CENTRAL) throw new Error('broken central directory');
    const method = view.getUint16(at + 10, true);
    const crc = view.getUint32(at + 16, true);
    const size = view.getUint32(at + 20, true);
    const nameLength = view.getUint16(at + 28, true);
    const extra = view.getUint16(at + 30, true);
    const comment = view.getUint16(at + 32, true);
    const local = view.getUint32(at + 42, true);
    const name = decoder.decode(archive.subarray(at + 46, at + 46 + nameLength));
    if (method !== 0) throw new Error(`entry ${name} is compressed (method ${method})`);
    if (view.getUint32(local, true) !== LOCAL) throw new Error(`broken local header for ${name}`);
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    const data = archive.slice(start, start + size);
    if (crc32(data) !== crc) throw new Error(`CRC mismatch for ${name}`);
    entries.push({ name, data });
    at += 46 + nameLength + extra + comment;
  }
  return entries;
}
